import { ReadonlyVec2, vec3 } from 'gl-matrix';
import { PlaneView } from '../../view/views/PlaneView';
import { View } from '../../view/View';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { ToolAction } from '../ToolAction';

/**
 * {@link ScrollAction} navigates through image slices
 * along the viewing direction of a {@link PlaneView}.
 *
 * This is a global action: always available regardless
 * of which tool is active.
 */
export class ScrollAction extends ToolAction {
    /**
     * Scroll one slice in the given direction.
     * @param view The PlaneView to scroll
     * @param delta  1 = backward/up the stack (PageUp / scroll-up),
     *              -1 = forward/down the stack  (PageDown / scroll-down)
     * @returns true if the slice was changed
     */
    private scrollSlice(view: PlaneView, delta: 1 | -1): boolean {
        if (!view.environment.image) {
            return false;
        }
        const direction =
            delta < 0
                ? view.viewDirection
                : vec3.negate(vec3.create(), view.viewDirection);
        const step = view.environment.image.calculateStep(
            view.planeIntersectionPoint.coordinates,
            direction
        );
        const newCoords = vec3.add(
            vec3.create(),
            view.planeIntersectionPoint.coordinates,
            step
        );
        view.updatePlaneIntersectionPoint(newCoords);
        return true;
    }

    public onMouseWheel(
        delta: 1 | -1,
        _modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this.scrollSlice(view, delta)) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        view: View
    ): EventHandlerResult {
        if (!(view instanceof PlaneView)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        let delta: 1 | -1 | null = null;
        if (key === 'PageUp' || key === 'ArrowUp') {
            delta = 1;
        } else if (key === 'PageDown' || key === 'ArrowDown') {
            delta = -1;
        }
        if (delta !== null && this.scrollSlice(view, delta)) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }
}
